import { Formik } from 'formik';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import Page from 'App/components/Page';
import { upsertMedicationAndGPI10Mapping } from 'App/services/AdminService';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted';
import toast from 'Shared/toast';

import AddEditMedicationDetailsForm from './components/AddEditMedicationDetailsForm';
import FindMedispanMedicationForm from './components/FindMedispanMedicationForm';
import ActionButtons from './components/ActionButtons';
import Error from './components/Error';

const validationSchema = yup.object().shape({
    assignedTherapeuticCategories: yup.array(),
    gpi10: yup.string(),
    medicationName: yup.string(),
    stmMedicationName: yup.string().required('STM Medication Name is required'),
});

const initialValues = {
    assignedTherapeuticCategories: [],
    gpi10: '',
    hasSearched: false,
    medicationName: '',
    medispanMedicationData: {
        results: [],
        total: 0,
    },
    searchParams: {
        sortKey: 'productName',
        sortOrder: 'asc',
        count: 10,
        page: 0,
    },
    stmMedicationName: '',
};

const AddMedication = () => {
    const [errorMessage, setErrorMessage] = useState(null);
    const isClinicalAdmin = useIsPermitted(PERMISSION_TYPES.CLINICAL_ADMIN);
    const permissionType = isClinicalAdmin
        ? PERMISSION_TYPES.ENABLE_CODIFY_MEDICATIONS
        : PERMISSION_TYPES.CLINICAL_ADMIN;
    const navigate = useNavigate();
    const location = useLocation();
    const navigateBack = () =>
        navigate(`/therapeutic-categories${location.state?.therapyId ? `/${location.state.therapyId}` : ''}`);

    return (
        <Page title="STM Medication" showPageTitle permissionType={permissionType}>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onReset={() => setErrorMessage(null)}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                    const newMedicationName = values.stmMedicationName?.trim();
                    const therapeuticCategoryIds = values.assignedTherapeuticCategories.map(
                        (therapy) => therapy?.id || therapy?.value
                    );
                    const gpi10 = values.gpi10 || null;
                    setErrorMessage(null);
                    try {
                        await upsertMedicationAndGPI10Mapping({ gpi10, newMedicationName, therapeuticCategoryIds });
                        toast.success('The medication has been added successfully.');
                        resetForm();
                    } catch (error) {
                        setErrorMessage(
                            error?.response?.data?.message || 'Failed to add medication. Please try again.'
                        );
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ handleSubmit, resetForm }) => {
                    return (
                        <>
                            <AddEditMedicationDetailsForm />
                            <FindMedispanMedicationForm />
                            <Error errorMessage={errorMessage} />
                            <ActionButtons onReset={resetForm} onCancel={navigateBack} onSave={handleSubmit} />
                        </>
                    );
                }}
            </Formik>
        </Page>
    );
};

export default AddMedication;
